
import { AxiosResponse } from "axios";
import { ReportTypeEnum, FilterModuleEnum, ReportStatesEnum, ReportStatusEnum } from "../constants/constants";
import { API_URL } from "../constants/url-config";
import { EcapModuleUploadFileModel, EcapReportModel } from "../models/ecap-report.model";
import { getAsync, patchAsync, postAsync, uploadFileAsync } from "../utils/http-client";


const serviceUrl = API_URL.ecapService;

const apiPaths = {
    loss_events: "/erm/loss-events/reports",
    scenario: "/erm/scenarios/reports",
    mitigation: "/scenario-mitigation",
    appraisal: "/appraisal-tool",
    reports: "/reports",
    summary: "/reports/summary",
    rapm: "/rapm/reports",
    ecapReports: "/ecap/reports",
    uploadFile: "/ecap/reports/upload-raw-file",
    comments: "/comments",
    resources: "/ecap/reports/resources",
    operationRisk: "/ecap/reports/operation-risk/",
    businessRisk: "/ecap/reports/business-risk/",
    uploadJsonData: "/ecap/reports/upload-json-data"
}

const getReportAsyns = async (status?: ReportStatusEnum, module?: FilterModuleEnum): Promise<AxiosResponse> => {
    const params = {
        status: status,
        module: module
    }
    return await getAsync(serviceUrl, apiPaths.reports, params, false);
}

const approveOrReferLossEventAsync = async (id: number, status: string) => {
    return await patchAsync(serviceUrl, `${apiPaths.loss_events}/${id}`, { status });
}

const approveOrReferScenarioAsync = async (id: number, status: string) => {
    return await patchAsync(serviceUrl, `${apiPaths.scenario}/${id}`, { status });
}

const approveOrReferMitigationAsync = async (scenarioId: number, status: string) => {
    return await patchAsync(serviceUrl, `${apiPaths.scenario}/${scenarioId}${apiPaths.mitigation}`, { status });
}

const approveOrReferAppraisalAsync = async (scenarioId: number, status: string) => {
    return await patchAsync(serviceUrl, `${apiPaths.scenario}/${scenarioId}${apiPaths.appraisal}`, { status });
}

const getReportSummary = async (status?: ReportStatusEnum) => {
    const params = {
        status: status
    }
    return await getAsync(serviceUrl, apiPaths.summary, params, false);
}

const approveOrReferRapmAsync = async (id: number, status: string) => {
    return await patchAsync(serviceUrl, `${apiPaths.rapm}/${id}`, { status });
}

const getEcapReports = async (reportType?: ReportTypeEnum): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, apiPaths.ecapReports, { risk_type: reportType }, false);
}

const createEcapReport = async (model: EcapReportModel): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, apiPaths.ecapReports, model);
}

const uploadFile = async (model: EcapModuleUploadFileModel): Promise<AxiosResponse> => {
    return await uploadFileAsync(serviceUrl, apiPaths.uploadFile, model, undefined, false);
}

const getEcapReportDetails = async (id: number, loading: boolean, jobId?: string): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, `${apiPaths.ecapReports}/${id}`, jobId ? { job: jobId } : undefined, loading);
}

const submitEcapReportForApproval = async (model: EcapReportModel): Promise<AxiosResponse> => {
    return await patchAsync(serviceUrl, `${apiPaths.ecapReports}/${model.id}`, { submit_state: ReportStatesEnum.Submitted, dataset_id: model.dataset_id });
}

const getEcapCommentsAsync = async (id: number): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, `${apiPaths.ecapReports}/${id}${apiPaths.comments}`, undefined, false);
}

const createEcapCommentAsync = async (id: number, message: string): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, `${apiPaths.ecapReports}/${id}${apiPaths.comments}`, { message }, undefined, false);
}

const approveOrReferEcapReportAsync = async (id: number, status: string) => {
    return await patchAsync(serviceUrl, `${apiPaths.ecapReports}/${id}`, { status });
}

const getResources = async () => {
    return await getAsync(serviceUrl, `${apiPaths.resources}`, undefined, false);
}

const createOperationRisk = async (model: EcapReportModel): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, apiPaths.operationRisk, model);
}

const createBusinessRisk = async (model: EcapReportModel): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, apiPaths.businessRisk, model);
}

const uploadJsonData = async (model: any): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, apiPaths.uploadJsonData, model);
}

export const ECAPService = {
    getReportAsyns,
    approveOrReferLossEventAsync,
    approveOrReferScenarioAsync,
    approveOrReferMitigationAsync,
    approveOrReferAppraisalAsync,
    getReportSummary,
    approveOrReferRapmAsync,
    getEcapReports,
    createEcapReport,
    uploadFile,
    getEcapReportDetails,
    submitEcapReportForApproval,
    getEcapCommentsAsync,
    createEcapCommentAsync,
    approveOrReferEcapReportAsync,
    getResources,
    createOperationRisk,
    uploadJsonData,
    createBusinessRisk
};
